import { useState, useEffect, useRef, useMemo } from 'react';
import InputField from '@/components/common/InputField';
import { Column, ColumnOption } from '@/types/columns';
import { toastMessage } from '@/utils/toast-message';
import { interactionNotice } from '@/utils/interaction-notice';
import { useUser } from '@/contexts/UserContext';
import { DispositionProvider, useDisposition } from '@/contexts/DispositionContext';
import { BucketType } from '@/types/disposition';
import type { ActiveIssue } from '@/components/LeadDetails/ActiveIssuesBar';
import api from '@/utils/api';

// Define types locally since they're not exported from a central location
// All fields are now directly in crm_leads table
interface LeadData {
    uuid?: string;
    lead_id?: number;
    // Basic fields
    name?: string;
    email?: string;
    phone?: string;
    message?: string;
    created_date?: string;
    last_updated_date?: string;
    // Status & Source
    status?: number | string;
    status_uuid?: string;
    source?: number | string;
    source_uuid?: string;
    statusOption?: { uuid: string; opt_name: string };
    sourceOption?: { uuid: string; opt_name: string };
    customerTypeOption?: { uuid: string; opt_name: string };
    // Address fields
    address_1?: string;
    address_2?: string;
    city?: string;
    country?: string;
    province?: string;
    zip?: string;
    // Custom fields (now in main table)
    label?: number | string;
    age?: string;
    gender?: string;
    concern?: string;
    call_status?: string;
    sub_source?: string;
    is_qualified?: boolean;
    // Shopify fields
    shopify_customer_id?: string;
    checkout_id?: string;
    checkout_token?: string;
    checkout_url?: string;
    checkout_date?: string;
    cart_value?: number;
    currency?: string;
    customer_type?: string;
    cart_token?: string;
    order_id?: string;
    // UTM fields
    utm_source?: string;
    utm_campaign?: string;
    utm_medium?: string;
    utm_content?: string;
    utm_term?: string;
    // Assignments
    assignments?: Array<{
        is_active: boolean;
        user_uuid?: string;
        user?: { uuid: string; userName?: string };
    }>;
    // Lead bucket & disposition
    // The lifecycle bucket. 'customer' is a real value here and has NO disposition
    // tree — use disposition_bucket below to pick a tree, never this.
    lead_bucket?: 'abandoned_cart' | 'reorder' | 'rto' | 'edd' | 'phone' | 'customer' | null;
    // Resolved server-side: always a bucket that has a tree.
    disposition_bucket?: BucketType | null;
    disposition_l1?: number | null;
    disposition_l2?: number | null;
    disposition_l3?: number | null;
    // Allow dynamic access for other fields
    [key: string]: any;
}

type Option = ColumnOption;
import Loader from '@/components/common/Loader/Loader';
import { fetchLeadColumns, getLeadDetails, createLead, updateLead } from '@/actions/lead-manager';
import ColumnsService from '@/utils/columnsCache';
import Button from '@/components/common/Button';

interface FormValues {
    [key: string]: string | number | null; // Key will be column UUID instead of col_name
}

// Store column information for lookup by UUID
interface ColumnLookup {
    [uuid: string]: Column;
}

interface DynamicLeadFormProps {
    onSubmitSuccess?: () => void;
    mode?: 'create' | 'update';
    leadData?: LeadData;
    leadUuid?: string | null;
    // The shipment the agent is working, when they came from a per-order screen.
    // Saved with the disposition so a customer with two open orders doesn't end up
    // with one outcome that could mean either.
    orderUuid?: string | null;
    // The EDD / NDR / RTO work item being worked. Its disposition is its own: the form
    // shows and saves THAT (with issue_uuid), and the customer's disposition is left
    // alone. Null/reorder = the customer's disposition, as before.
    workItem?: ActiveIssue | null;
    onClose?: () => void; // Add onClose prop for modal closure
    refreshData?: () => void;
    activeUsersForDropdown?: UserOption[]; // Users passed from parent to avoid API call
}

interface UserOption {
    value: string;
    label: string;
    email?: string;
    role?: string;
}

// Inline component for rendering form table with disposition fields
interface DispositionFieldsTableProps {
    columns: Column[];
    renderField: (column: Column) => React.ReactNode;
    leadDetails: LeadData | null;
    mode: 'create' | 'update';
    onDispositionUpdate: (l1: number | null, l2: number | null, l3: number | null) => void;
}

const DispositionFieldsTable: React.FC<DispositionFieldsTableProps> = ({
    columns,
    renderField,
    leadDetails,
    mode,
    onDispositionUpdate
}) => {
    const { getTree, getL1Options, getL2Options, getL3Options, loading: dispLoading } = useDisposition();
    const [l1Value, setL1Value] = useState<number | null>(leadDetails?.disposition_l1 || null);
    const [l2Value, setL2Value] = useState<number | null>(leadDetails?.disposition_l2 || null);
    const [l3Value, setL3Value] = useState<number | null>(leadDetails?.disposition_l3 || null);
    const [l1Options, setL1Options] = useState<Array<{ option_id: number; value: string }>>([]);
    const [l2Options, setL2Options] = useState<Array<{ option_id: number; value: string }>>([]);
    const [l3Options, setL3Options] = useState<Array<{ option_id: number; value: string }>>([]);
    const [treeLoaded, setTreeLoaded] = useState(false);

    // Which disposition tree to work this lead with. NOT lead_bucket: `customer`
    // (ordered, nothing delivered yet) has no tree of its own, and casting it to
    // BucketType sent /dispositions/tree/customer, which 400s. The catch below
    // swallowed it, treeLoaded stayed false, and the dropdowns silently rendered
    // empty for all 65,893 customer-bucket leads.
    //
    // The API now resolves it server-side: a customer lead gets the tree of whatever
    // issue is open on their orders, since that is the call the agent is making.
    // The || fallback is for older cached payloads that predate the field.
    const bucketType = (leadDetails?.disposition_bucket
        || leadDetails?.lead_bucket
        || 'abandoned_cart') as BucketType;
    const showDisposition = mode === 'update';

    // Load disposition tree
    useEffect(() => {
        if (showDisposition) {
            getTree(bucketType).then(() => setTreeLoaded(true)).catch(console.error);
        }
    }, [bucketType, showDisposition, getTree]);

    // Update L1 options
    useEffect(() => {
        if (treeLoaded) {
            setL1Options(getL1Options(bucketType));
        }
    }, [treeLoaded, bucketType, getL1Options]);

    // Update L2 options when L1 changes
    useEffect(() => {
        if (treeLoaded && l1Value) {
            setL2Options(getL2Options(bucketType, l1Value));
        } else {
            setL2Options([]);
        }
    }, [treeLoaded, bucketType, l1Value, getL2Options]);

    // Update L3 options when L2 changes
    useEffect(() => {
        if (treeLoaded && l2Value) {
            setL3Options(getL3Options(bucketType, l2Value));
        } else {
            setL3Options([]);
        }
    }, [treeLoaded, bucketType, l2Value, getL3Options]);

    // Sync with leadDetails
    useEffect(() => {
        setL1Value(leadDetails?.disposition_l1 || null);
        setL2Value(leadDetails?.disposition_l2 || null);
        setL3Value(leadDetails?.disposition_l3 || null);
    }, [leadDetails?.disposition_l1, leadDetails?.disposition_l2, leadDetails?.disposition_l3]);

    // Only update local state - no auto-save, submit with form
    const handleL1Change = (optionId: number | null) => {
        setL1Value(optionId);
        setL2Value(null);
        setL3Value(null);
    };

    const handleL2Change = (optionId: number | null) => {
        setL2Value(optionId);
        setL3Value(null);
    };

    const handleL3Change = (optionId: number | null) => {
        setL3Value(optionId);
    };

    // Expose current values via callback for parent to use on submit
    useEffect(() => {
        onDispositionUpdate(l1Value, l2Value, l3Value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [l1Value, l2Value, l3Value]);

    // Custom field order: Phone, Name, then L1/L2/L3, then Email, Customer Type, Source, Created Date
    const fieldOrder = ['phone', 'name'];
    const afterDispositionFields = ['email', 'customer_type', 'source', 'created_date', 'createdDate'];
    const hiddenFields = ['status']; // Fields to hide from the form

    // Separate columns into before-disposition, after-disposition, and others (excluding hidden fields)
    const beforeDisposition = columns.filter(c => fieldOrder.includes(c.col_name) && !hiddenFields.includes(c.col_name));
    const afterDisposition = columns.filter(c => afterDispositionFields.includes(c.col_name) && !hiddenFields.includes(c.col_name));
    const otherColumns = columns.filter(c => !fieldOrder.includes(c.col_name) && !afterDispositionFields.includes(c.col_name) && !hiddenFields.includes(c.col_name));

    // Sort each group
    const sortByOrder = (arr: typeof columns) => [...arr].sort((a, b) => {
        const orderA = fieldOrder.indexOf(a.col_name);
        const orderB = fieldOrder.indexOf(b.col_name);
        if (orderA !== -1 && orderB !== -1) return orderA - orderB;
        const afterOrderA = afterDispositionFields.indexOf(a.col_name);
        const afterOrderB = afterDispositionFields.indexOf(b.col_name);
        if (afterOrderA !== -1 && afterOrderB !== -1) return afterOrderA - afterOrderB;
        return a.col_order - b.col_order;
    });

    const selectClass = 'w-full p-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white';

    // Render a table row for a column
    const renderColumnRow = (column: typeof columns[0], rowIndex: number) => (
        <tr key={column.uuid} className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}>
            <td className="py-3 px-4 text-sm font-medium text-gray-700 w-1/3 align-top border-b border-gray-200">
                {column.col_label}
                {column.col_required && <span className="text-red-500 ml-1">*</span>}
            </td>
            <td className="py-2 px-4 w-2/3 border-b border-gray-200">
                {renderField(column)}
            </td>
        </tr>
    );

    // Render disposition row (L1, L2, L3 are mandatory)
    const renderDispositionRow = (label: string, value: number | null, options: Array<{ option_id: number; value: string }>, onChange: (id: number | null) => void, disabled: boolean, rowIndex: number) => (
        <tr key={label} className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}>
            <td className="py-3 px-4 text-sm font-medium text-gray-700 w-1/3 align-top border-b border-gray-200">
                {label}
                <span className="text-red-500 ml-1">*</span>
            </td>
            <td className="py-2 px-4 w-2/3 border-b border-gray-200">
                <select
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                    disabled={dispLoading || disabled}
                    className={`${selectClass} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${!value && !disabled ? 'border-red-300' : ''}`}
                >
                    <option value="">Select {label}</option>
                    {options.map((opt) => (
                        <option key={opt.option_id} value={opt.option_id}>{opt.value}</option>
                    ))}
                </select>
            </td>
        </tr>
    );

    let rowIndex = 0;

    return (
        <table className="w-full border-collapse">
            <tbody>
                {/* Phone, Name first */}
                {sortByOrder(beforeDisposition).map((column) => renderColumnRow(column, rowIndex++))}

                {/* Lead Status 1, 2, 3 after Name */}
                {showDisposition && (
                    <>
                        {renderDispositionRow('Lead Status 1', l1Value, l1Options, handleL1Change, !treeLoaded, rowIndex++)}
                        {renderDispositionRow('Lead Status 2', l2Value, l2Options, handleL2Change, !l1Value || l2Options.length === 0, rowIndex++)}
                        {renderDispositionRow('Lead Status 3', l3Value, l3Options, handleL3Change, !l2Value || l3Options.length === 0, rowIndex++)}
                    </>
                )}

                {/* Email, Customer Type, Source, Created Date */}
                {sortByOrder(afterDisposition).map((column) => renderColumnRow(column, rowIndex++))}

                {/* Any other columns at the end */}
                {sortByOrder(otherColumns).map((column) => renderColumnRow(column, rowIndex++))}
            </tbody>
        </table>
    );
};

const DynamicLeadForm = ({
    onSubmitSuccess,
    mode = 'create',
    leadData,
    leadUuid,
    orderUuid,
    workItem = null,
    onClose,
    refreshData,
    activeUsersForDropdown = []
}: DynamicLeadFormProps) => {
    const [columns, setColumns] = useState<Column[]>([]);
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState<FormValues>({});
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const [leadDetails, setLeadDetails] = useState<LeadData | null>(leadData || null);

    const [remarks, setRemarks] = useState<string>('');
    const [remarksError, setRemarksError] = useState<string>('');
    const [activeUsers, setActiveUsers] = useState<UserOption[]>([]);
    const [selectedOwner, setSelectedOwner] = useState<string>('');
    // Only an owner someone actually picked in the dropdown is sent on save. Sending the
    // value loaded with the form handed customers back to a previous agent (Sep 2026).
    const [ownerTouched, setOwnerTouched] = useState(false);
    const [assignedOwnerName, setAssignedOwnerName] = useState<string>(''); // Track owner name directly from assignment

    // Disposition values from the form (saved on submit, not on change)
    const [dispositionL1, setDispositionL1] = useState<number | null>(null);
    const [dispositionL2, setDispositionL2] = useState<number | null>(null);
    const [dispositionL3, setDispositionL3] = useState<number | null>(null);

    // Check if user is Super Administrator and role permissions
    const { userData } = useUser();
    const isSuperAdmin = userData?.data?.role?.role_id === 9401;
    const userRoleId = userData?.data?.role?.role_id;

    // Role IDs that cannot change the owner
    const restrictedRoleIds = [9406, 9407, 9408, 9401];
    const canChangeOwner = isSuperAdmin || !userRoleId || !restrictedRoleIds.includes(userRoleId);

    // Refs for scroll functionality
    const remarksFieldRef = useRef<HTMLDivElement>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);

    // Fetch columns and lead data when component mounts
    useEffect(() => {
        fetchFormColumns();
        if (mode === 'update' && activeUsersForDropdown.length > 0) {
            // Use the prop instead of fetching
            setActiveUsers(activeUsersForDropdown);
        }
    }, [leadUuid, mode, activeUsersForDropdown]);

    // Map lead data to form values
    useEffect(() => {
        if (leadDetails && columns.length > 0) {
            mapLeadDataToFormValues();
        }
    }, [leadDetails, columns]);

    // Where the disposition fields read from: the selected work item's own box when it
    // has one, otherwise the customer's. Everything else on the form is the customer.
    const itemScoped = workItem?.disposition_scope === 'work_item';
    const dispositionSource = useMemo<LeadData | null>(() => {
        if (!leadDetails || !itemScoped || !workItem) return leadDetails;
        return {
            ...leadDetails,
            disposition_bucket: (workItem.disposition_bucket as BucketType) ?? leadDetails.disposition_bucket,
            disposition_l1: workItem.disposition_l1 ?? null,
            disposition_l2: workItem.disposition_l2 ?? null,
            disposition_l3: workItem.disposition_l3 ?? null,
        };
    }, [leadDetails, itemScoped, workItem]);

    // Initialize disposition values from whichever box the form is working
    useEffect(() => {
        if (dispositionSource) {
            setDispositionL1(dispositionSource.disposition_l1 || null);
            setDispositionL2(dispositionSource.disposition_l2 || null);
            setDispositionL3(dispositionSource.disposition_l3 || null);
        }
    }, [dispositionSource]);

    // Set the assigned owner name from lead data
    useEffect(() => {
        if (!leadDetails) return;
        setOwnerTouched(false);

        // Try from assignments first (backend now includes user.userName)
        if (leadDetails.assignments && leadDetails.assignments.length > 0) {
            const activeAssignment = leadDetails.assignments.find((a: any) => a.is_active) || leadDetails.assignments[0];
            if (activeAssignment) {
                const ownerName = activeAssignment.user?.userName || (activeAssignment as any).user_name || leadDetails.owner_name || '';
                if (ownerName) setAssignedOwnerName(ownerName);

                const ownerUuid = activeAssignment.user_uuid || activeAssignment.user?.uuid;
                if (ownerUuid) setSelectedOwner(ownerUuid);
                return;
            }
        }

        // Fallback to denormalized owner_name / owner_uuid on the lead itself
        if (leadDetails.owner_name) {
            setAssignedOwnerName(leadDetails.owner_name);
        }
        if (leadDetails.owner_uuid) {
            setSelectedOwner(leadDetails.owner_uuid);
        }
    }, [leadDetails]);

    const handleRemarksChange = (value: string) => {
        setRemarks(value);
        if (remarksError) {
            setRemarksError('');
        }
    };

    // REMOVED: fetchActiveUsers - now using prop passed from parent (Leads page)
    // This eliminates redundant API calls on every lead edit
    // const fetchActiveUsers = async () => { ... }

    const fetchFormColumns = async () => {
        try {
            setLoading(true);
            // Try to get columns from cache first, then fallback to fetchLeadColumns
            // Note: No need to force refresh in update mode - 30min cache is sufficient
            let response = await ColumnsService.getColumns(true);
            
            // If cache service fails, fallback to original method
            if (!response.success) {
                console.log('Cache failed, falling back to fetchLeadColumns');
                response = await fetchLeadColumns();
            }

            if (response.success && response.columns) {
                const activeColumns = response.columns;
                setColumns(activeColumns);

                // Create a lookup object indexed by column UUID
                const lookup: ColumnLookup = {};
                activeColumns.forEach(column => {
                    lookup[column.uuid] = column;
                });

                // Initialize form values with empty strings or default values using UUID as keys
                const initialValues: FormValues = {};
                activeColumns.forEach(column => {
                    if (column.col_type === 'select' && column.options.length > 0) {
                        // For select fields, use the "please_select" value as default if exists
                        const defaultOption = column.options.find(opt => opt.opt_value === 'please_select');
                        initialValues[column.uuid] = defaultOption ? defaultOption.opt_value : '';
                    } else {
                        initialValues[column.uuid] = '';
                    }
                });

                setFormValues(initialValues);

                // If we're in update mode
                if (mode === 'update') {
                    if (leadData) {
                        // Use provided data immediately for instant display
                        setLeadDetails(leadData);

                        // Only re-fetch if leadData is missing critical information (not just assignments)
                        // Removed the assignments check to prevent unnecessary API calls
                        // The parent component should provide complete leadData when opening the form
                    } else if (leadUuid) {
                        // No data provided, fetch fresh
                        fetchLeadDetailsById(leadUuid);
                    }
                } else if (leadData && mode === 'create') {
                    // For create mode, use provided data if available
                    setLeadDetails(leadData);
                }
            } else {
                setColumns([]);
            }
        } catch (error) {
            console.error('Error fetching columns:', error);
            setColumns([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch lead details for update mode
    const fetchLeadDetailsById = async (uuid: string) => {
        try {
            setLoading(true);
            const response = await getLeadDetails(uuid);

            if (response.success && response.data) {
                setLeadDetails(response.data);
            }
        } catch (error) {
            console.error('Error fetching lead details:', error);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to format date for datetime-local input
    const formatDateForInput = (dateValue: any): string | null => {
        if (!dateValue) return null;
        try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return null;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (err) {
            console.error('Error formatting date:', err);
            return null;
        }
    };

    // Map lead data to form values for update mode
    // All fields are now directly in crm_leads table - no separate custom fields table
    const mapLeadDataToFormValues = () => {
        if (!leadDetails) {
            return;
        }

        const mappedValues: FormValues = {};
        const leadData = leadDetails as any; // Cast for dynamic property access

        // Iterate through all columns and map values from lead object
        columns.forEach(column => {
            const colName = column.col_name;
            let value = leadData[colName];

            // Skip if no value exists
            if (value === undefined || value === null) {
                return;
            }

            // Handle different column types
            if (column.col_type === 'select') {
                // For select fields, check for _uuid suffix first, then try to find option by opt_id or opt_value
                const uuidField = `${colName}_uuid`;
                if (leadData[uuidField]) {
                    // Use UUID if available (status_uuid, source_uuid, etc.)
                    mappedValues[column.uuid] = leadData[uuidField];
                } else if (column.options && column.options.length > 0) {
                    // Try to find matching option by opt_id (integer or string) or opt_value (string)
                    const numericValue = typeof value === 'string' ? parseInt(value, 10) : value;
                    const matchingOption = column.options.find(opt =>
                        opt.opt_id === value || opt.opt_id === numericValue || opt.opt_value === value
                    );
                    if (matchingOption) {
                        mappedValues[column.uuid] = matchingOption.uuid;
                    } else {
                        // Fallback: use value as-is
                        mappedValues[column.uuid] = value;
                    }
                } else {
                    mappedValues[column.uuid] = value;
                }
            } else if (column.col_type === 'dateTime' || column.col_type === 'datetime') {
                // Format date for datetime-local input
                const formattedDate = formatDateForInput(value);
                if (formattedDate) {
                    mappedValues[column.uuid] = formattedDate;
                }
            } else if (column.col_type === 'date') {
                // Format date for date input (YYYY-MM-DD)
                if (value) {
                    try {
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) {
                            mappedValues[column.uuid] = date.toISOString().split('T')[0];
                        }
                    } catch (err) {
                        mappedValues[column.uuid] = value;
                    }
                }
            } else {
                // Text, number, textarea, etc. - use value directly
                mappedValues[column.uuid] = value;
            }
        });

        // Also check for statusOption and sourceOption from API (for display purposes)
        if (leadData.statusOption?.uuid) {
            const statusColumn = columns.find(col => col.col_name === 'status');
            if (statusColumn) {
                mappedValues[statusColumn.uuid] = leadData.statusOption.uuid;
            }
        }
        if (leadData.sourceOption?.uuid) {
            const sourceColumn = columns.find(col => col.col_name === 'source');
            if (sourceColumn) {
                mappedValues[sourceColumn.uuid] = leadData.sourceOption.uuid;
            }
        }

        // Handle customer_type - use customerTypeOption from API (like status/source)
        if (leadData.customerTypeOption?.uuid) {
            const customerTypeColumn = columns.find(col => col.col_name === 'customer_type');
            if (customerTypeColumn) {
                mappedValues[customerTypeColumn.uuid] = leadData.customerTypeOption.uuid;
            }
        }

        console.log('📋 Mapped form values:', Object.keys(mappedValues).length, 'fields');
        setFormValues(prev => ({ ...prev, ...mappedValues }));
    };

    const handleChange = (uuid: string, value: string | number) => {
        setFormValues(prev => ({
            ...prev,
            [uuid]: value
        }));

        // Clear error for this field if it exists
        if (fieldErrors[uuid]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[uuid];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required fields
        const requiredFields = columns.filter(col => col.col_required);
        const missingFields = requiredFields.filter(field => {
            const value = formValues[field.uuid];
            return value === null || value === undefined || value === '';
        });

        if (missingFields.length > 0) {
            // Set individual field errors
            const newErrors: { [key: string]: string } = {};
            missingFields.forEach(field => {
                newErrors[field.uuid] = `${field.col_label} is required`;
            });
            setFieldErrors(newErrors);
            toastMessage(`Please fill in required fields: ${missingFields.map(f => f.col_label).join(', ')}`, 'error')
            return;
        }

        // Validate remarks for edit mode
        if (mode === 'update' && (!remarks || remarks.trim() === '')) {
            setRemarksError('Please provide a reason for editing this lead');
            toastMessage('Please provide a reason for editing this lead', 'error');

            // Scroll to remarks field
            if (remarksFieldRef.current) {
                remarksFieldRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                // Also focus on the textarea for better UX
                const textarea = remarksFieldRef.current.querySelector('textarea');
                if (textarea) {
                    setTimeout(() => {
                        textarea.focus();
                    }, 300);
                }
            }

            return;
        }

        // Validate disposition fields for update mode
        // Dispositions are optional (e.g. a fresh phone lead may not have one yet) — only
        // enforced once the agent starts setting one, since a partial L1-only disposition
        // (without required L2/L3) would otherwise get saved.
        // L2 and L3 are required only if L1 is set and is NOT "New Lead"
        // New Lead option_id: 1 (abandoned_cart) or 47 (reorder)
        if (mode === 'update' && dispositionL1) {
            const isNewLeadDisposition = dispositionL1 === 1 || dispositionL1 === 47;
            const missingStatus: string[] = [];

            if (!isNewLeadDisposition) {
                if (!dispositionL2) missingStatus.push('Lead Status 2');
                if (!dispositionL3) missingStatus.push('Lead Status 3');
            }

            if (missingStatus.length > 0) {
                toastMessage(`Please select ${missingStatus.join(', ')}`, 'error');
                return;
            }
        }

        try {
            setLoading(true);
            let response;

            if (mode === 'create') {
                // For create mode, find standard fields and map them by name
                const formData: { [key: string]: any } = {};

                // Find standard fields by their col_name and map them accordingly
                columns.forEach(column => {
                    const value = formValues[column.uuid];
                    formData[column.uuid] = value;
                });

                const createData = {
                    formData: formData
                };

                response = await createLead(createData);

                if (response.success) {
                    // Use the response message if available, otherwise use a default message
                    const successMessage = response.message || (mode === 'create' ? 'Lead created successfully!' : 'Lead updated successfully!');
                    toastMessage(successMessage, 'success');

                    // Close the modal if onClose prop exists
                    if (onClose) {
                        onClose();
                    }

                    // Refresh the data if refreshData prop exists
                    if (refreshData) {
                        await refreshData();
                    }

                    // Call onSubmitSuccess callback if provided
                    if (onSubmitSuccess) {
                        onSubmitSuccess();
                    }
                }

            } else {
                // Update existing lead
                if (!leadDetails || !leadDetails.uuid) {
                    toastMessage('Lead UUID is missing for update', 'warning');
                    setLoading(false);
                    return;
                }

                // For update, prepare only the enabled fields
                const updateFormData: { [key: string]: any } = {};

                // Identify disabled fields in update mode
                const disabledFields = ['phone', 'source', 'created_date', 'createdDate', 'message'];

                // Add only enabled fields to the updateData
                columns.forEach(column => {
                    // Skip disabled fields in update mode
                    if (!disabledFields.includes(column.col_name)) {
                        updateFormData[column.uuid] = formValues[column.uuid];
                    }
                });

                updateFormData['remarks'] = remarks;

                // Update disposition for update mode
                let interactionOwner: string | null | undefined;
                if (dispositionL1) {
                    try {
                        const dispRes: any = await api.req({
                            url: `/dispositions/update/${leadDetails.uuid}`,
                            method: api.Method.POST,
                            body: {
                                l1: dispositionL1,
                                l2: dispositionL2,
                                l3: dispositionL3,
                                remarks: remarks.trim() || null,
                                order_uuid: orderUuid || null,
                                issue_uuid: itemScoped ? workItem?.issue_uuid ?? null : null
                            }
                        });
                        if (dispRes?.data?.interaction) interactionOwner = dispRes.data.owner_name ?? '';
                    } catch (dispError) {
                        console.error('Failed to update disposition:', dispError);
                        toastMessage('Failed to update disposition', 'error');
                        setLoading(false);
                        return;
                    }
                }

                response = await updateLead(leadDetails.uuid, updateFormData, remarks.trim(),
                                            ownerTouched ? selectedOwner : undefined);

                if (response.success) {
                    if (interactionOwner !== undefined) {
                        toastMessage(interactionNotice(interactionOwner), 'info');
                    } else {
                        toastMessage('Lead updated successfully!', 'success');
                    }

                    // Reset remarks after successful update
                    setRemarks('');
                    setRemarksError('');

                    // Close the modal if onClose prop exists
                    if (onClose) {
                        onClose();
                    }
                    if (refreshData) {
                        refreshData();
                    }
                } else {
                    toastMessage('Failed to update lead', 'error');
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toastMessage('An error occurred while processing your request', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Map column.options to InputField options format
    const mapOptions = (options: Option[]) => {
        return options
            .sort((a, b) => a.opt_order - b.opt_order)
            .map(option => ({
                value: option.uuid, // Use UUID for value to match the API response
                label: option.opt_name
            }));
    };

    // Render different field types using InputField component
    const renderField = (column: Column) => {
        const { uuid, col_name, col_label, col_type, col_required, col_readonly, col_description, options } = column;

        // Type-safe value access - ensure we convert to string if needed for React props
        const fieldValue = formValues[uuid] !== null && formValues[uuid] !== undefined
            ? String(formValues[uuid])
            : '';

        // Disable phone, source, and created_date - edit
        const shouldDisableInEditMode = mode === 'update' &&
            (col_name === 'phone' ||
                col_name === 'source' ||
                col_name === 'created_date' ||
                col_name === 'createdDate' ||
                col_name === 'message'
            );

        // Fields that should be editable only if empty
        const editableOnlyIfEmpty = ['checkout_token', 'checkout_url', 'cart_token', 'shopify_customer_id', 'checkout_id'];
        const shouldDisableIfNotEmpty = mode === 'update' &&
            editableOnlyIfEmpty.includes(col_name) &&
            fieldValue !== '';

        // Common props for all field types
        const commonProps = {
            id: uuid,
            name: uuid,
            // Remove label since we're showing it separately in the grid layout
            required: col_required,
            disabled: loading || col_readonly || shouldDisableInEditMode || shouldDisableIfNotEmpty,
            placeholder: col_description || `Enter ${col_label}`,
            value: fieldValue,
            onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                handleChange(uuid, e.target.value);
            },
            error: !!fieldErrors[uuid],
            className: 'w-full'
        };

        // Special handling for checkout_url - add copy button
        if (col_name === 'checkout_url') {
            const handleCopy = () => {
                if (fieldValue) {
                    navigator.clipboard.writeText(fieldValue);
                    toastMessage('Checkout URL copied to clipboard', 'success');
                }
            };
            return (
                <div className="flex gap-2">
                    <InputField
                        {...commonProps}
                        type="text"
                        placeholder="https://..."
                    />
                    {fieldValue && (
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md text-gray-600 transition-colors"
                            title="Copy URL"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </button>
                    )}
                </div>
            );
        }

        // For select type, add options
        if (col_type === 'select') {
            return (
                <InputField
                    {...commonProps}
                    type="select"
                    options={mapOptions(options)}
                />
            );
        }

        // For textarea type
        if (col_type === 'textarea') {
            return (
                <InputField
                    {...commonProps}
                    type="textarea"
                    rows={1}
                />
            );
        }

        // For dateTime type
        if (col_type === 'dateTime') {
            return (
                <InputField
                    {...commonProps}
                    type="datetime-local"
                />
            );
        }

        // For date type
        if (col_type === 'date') {
            return (
                <InputField
                    {...commonProps}
                    type="date"
                />
            );
        }

        // For email type
        if (col_type === 'email') {
            return (
                <InputField
                    {...commonProps}
                    type="email"
                    placeholder="example@email.com"
                />
            );
        }

        // For number type
        if (col_type === 'number' || col_type === 'numeric') {
            return (
                <InputField
                    {...commonProps}
                    type="number"
                />
            );
        }

        // For phone type
        if (col_type === 'phone' || col_type === 'tel') {
            return (
                <InputField
                    {...commonProps}
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                />
            );
        }

        // For URL type
        if (col_type === 'url') {
            return (
                <InputField
                    {...commonProps}
                    type="text"
                    placeholder="https://example.com"
                />
            );
        }

        // For checkbox type (boolean)
        if (col_type === 'checkbox' || col_type === 'boolean') {
            return (
                <div className="flex items-center pt-2">
                    <input
                        id={uuid}
                        name={uuid}
                        type="checkbox"
                        checked={formValues[uuid] === '1' || formValues[uuid] === 1}
                        onChange={(e) => handleChange(uuid, e.target.checked ? 1 : 0)}
                        disabled={loading || col_readonly || shouldDisableInEditMode}
                        className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                        Check to enable
                    </span>
                    {fieldErrors[uuid] && (
                        <p className="ml-2 text-sm text-red-600">{fieldErrors[uuid]}</p>
                    )}
                </div>
            );
        }

        // For radio buttons (if col_type is radio and has options)
        if (col_type === 'radio' && options && options.length > 0) {
            return (
                <div>
                    <div className="space-y-2">
                        {mapOptions(options).map((option) => (
                            <div key={option.value} className="flex items-center">
                                <input
                                    id={`${uuid}-${option.value}`}
                                    name={uuid}
                                    type="radio"
                                    value={option.value}
                                    checked={formValues[uuid] === option.value}
                                    onChange={(e) => handleChange(uuid, e.target.value)}
                                    disabled={loading || col_readonly || shouldDisableInEditMode}
                                    className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300"
                                />
                                <label htmlFor={`${uuid}-${option.value}`} className="ml-2 text-sm text-gray-700">
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                    {fieldErrors[uuid] && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors[uuid]}</p>
                    )}
                </div>
            );
        }

        // Default to text input
        return (
            <InputField
                {...commonProps}
                type="text"
            />
        );
    };

    const renderRemarksField = () => {
        if (mode !== 'update') return null;

        return (
            <div className="w-full" ref={remarksFieldRef}>
                <textarea
                    id="remarks"
                    name="remarks"
                    required={true}
                    disabled={loading}
                    placeholder="Enter remarks for this update (required)"
                    value={remarks}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        handleRemarksChange(e.target.value);
                    }}
                    className={`w-full px-3 py-2 border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor transition-all duration-300 ${
                        remarksError
                            ? 'border-red-500 bg-red-50 animate-pulse'
                            : 'border-gray-300'
                    }`}
                    rows={3}
                />
                {remarksError && (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-red-100 border border-red-400 rounded-md animate-fade-in">
                        <svg className="h-5 w-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm font-medium text-red-800">{remarksError}</p>
                    </div>
                )}
            </div>
        );
    };

    if (loading && columns.length === 0) {
        return (
            <div className="flex items-center justify-center">
                <Loader size={42} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" text="Please wait...." />
            </div>
        )
    }

    return (
        <div className="h-full flex flex-col">
            {loading && columns.length === 0 ? (
                <div className="flex items-center justify-center">
                    <Loader size={42} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" text="Please wait...." />
                </div>
            ) : (
                <>
                    {/* Main content area with form and sticky remarks */}
                    <div className={`flex-1 ${mode === 'update' ? 'flex gap-4' : ''}`} ref={formContainerRef}>
                        {/* Left side - Scrollable Form Fields */}
                        <div className={`${mode === 'update' ? 'flex-1 overflow-y-auto' : 'overflow-y-auto'}`}>
                            <form onSubmit={handleSubmit}>
                                {/* Owner Information Section - Only for Update Mode */}
                                {mode === 'update' && (
                                    <div className="mb-4 bg-blue-50 p-3 rounded-lg border border-blue-200">
                                        <div className="flex items-center space-x-3">
                                            <label className="text-sm font-medium text-gray-700 w-20">
                                                Owner:
                                            </label>
                                            {!isSuperAdmin && (!canChangeOwner || !!assignedOwnerName) ? (
                                                <div className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-700">
                                                    {assignedOwnerName || 'Unassigned'}
                                                </div>
                                            ) : (
                                                <select
                                                    value={selectedOwner}
                                                    onChange={(e) => { setSelectedOwner(e.target.value); setOwnerTouched(true); }}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor"
                                                    disabled={loading}
                                                >
                                                    <option value="">Unassigned</option>
                                                    {activeUsers.map((user) => (
                                                        <option key={user.value} value={user.value}>
                                                            {user.label} {user.role && `(${user.role})`}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        {isSuperAdmin && assignedOwnerName && (
                                            <p className="text-xs text-blue-600 mt-1 ml-[84px]">
                                                As Super Administrator, you can reassign this lead
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Lead Form Fields Section */}
                                <div className="mb-6">
                                    <DispositionProvider>
                                        <DispositionFieldsTable
                                            columns={columns}
                                            renderField={renderField}
                                            leadDetails={dispositionSource}
                                            mode={mode}
                                            onDispositionUpdate={(l1, l2, l3) => {
                                                setDispositionL1(l1);
                                                setDispositionL2(l2);
                                                setDispositionL3(l3);
                                            }}
                                        />
                                    </DispositionProvider>
                                </div>
                            </form>
                        </div>

                        {/* Right side - Sticky Remarks (only for Update Mode) - 1:1 ratio with form */}
                        {mode === 'update' && (
                            <div className="flex-1 self-start sticky top-0" ref={remarksFieldRef}>
                                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 shadow-sm">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Update Remarks *</h4>
                                    {renderRemarksField()}
                                    <p className="text-xs text-gray-500 mt-2">
                                        Required for lead updates
                                    </p>
                                    {/* Update Lead button beneath remarks */}
                                    <Button
                                        onClick={handleSubmit}
                                        className="w-full mt-4 px-6 py-2 bg-brandColor text-white"
                                        disabled={loading}
                                    >
                                        {loading ? 'Updating...' : 'Update Lead'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sticky Button at Bottom - Only for Create Mode */}
                    {mode === 'create' && (
                        <div className="border-t bg-white p-4 sticky bottom-0">
                            <div className="flex justify-end space-x-3">
                                <Button
                                    onClick={handleSubmit}
                                    className="px-6 py-2 bg-brandColor text-white"
                                    disabled={loading}
                                >
                                    {loading ? 'Submitting...' : 'Create Lead'}
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default DynamicLeadForm;